import React, {
  FC,
  useEffect,
  useState,
  useCallback,
  useRef,
  HTMLAttributes,
  lazy,
  Suspense,
} from 'react';
import _ from 'lodash';
import { animateScroll } from 'react-scroll';
import { useErrorMonitor } from '@wix/fe-essentials-standalone';
import {
  marketplaceBriefGeneralAction,
  partnersProfilePagePageLoaded,
  copyOfExploreResultsPageGenericClick,
} from '@wix/bi-logger-marketplace/v2';
import { Box } from '@wix/design-system';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntersect } from '@wix/experts-common';
import { useBi } from '@components/Hooks/useBi';
import useMobileMediaQuery from '@components/Hooks/useMobileMediaQuery';
import { useProfileContext } from '@components/Contexts/ProfileContext';
import { useHeaderContext } from '@components/Contexts/HeaderContext';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import Header from '@components/Header';
import CompactBrief from '@components/CompactBrief';
import ErrorFallback from '@components/ErrorFallback';
import NavBar from './NavBar';
import ContactPanel from './ContactPanel';
import MiniContactPanel from './MiniContactPanel';
import About from './About';
import Services from './Services';
import FeaturedProjects from './FeaturedProjects';
import StartingRates from './StartingRates';
import ProviderLanguages from './ProviderLanguages';
import ProviderLocation from './ProviderLocation';
import { Sections } from './NavBar/NavBar';
import TraceableTraderData from './TraceableTraderData';
import s from './Profile.module.scss';
import CopyNotification from 'components/Profile_New/CopyNotification';
import { useCopyToClipboard } from '@components/Hooks/useCopyToClipboard';
import ProjectsReviewsWithPaging from '@components/Profile_New/ProjectsReviews/ProjectsReviewsWithPaging';
import ContactStatusToast from '@components/Profile_New/ContactStatusToast';
import BackToExplorer from './BackToExplorer';

const Certifications = lazy(() =>
  import('./Certifications').then((module) => ({
    default: module.default,
  })),
);

interface SectionWrapperProps extends HTMLAttributes<HTMLElement> {
  sectionId: string;
  children: any;
}

const SectionWrapper = React.forwardRef(
  (props: SectionWrapperProps, ref: any) => {
    const { sectionId, children } = props;
    return (
      <section
        id={sectionId}
        tabIndex={-1}
        ref={(el) => {
          if (ref?.current) {
            ref.current[sectionId] = el;
          }
        }}
        className={s.navigationSection}
        {...props}
      >
        {children}
      </section>
    );
  },
);

const Profile: FC = () => {
  const logger = useBi();
  const { sessionId } = useBiSessionContext();
  const { userDetails } = useHeaderContext();
  const {
    partnerProfile,
    entry,
    origin,
    appStatus,
    enabled,
    isRenderedInModal,
    isMobileUserAgent,
  } = useProfileContext();
  const isMobileView = useMobileMediaQuery(isMobileUserAgent);
  const errorMonitor = useErrorMonitor();
  const [isOpen, setIsOpen] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [ref, entryElement] = useIntersect({}) as any;
  const [showShadow, setShowShadow] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(false);
  const [briefTimedOutMessage, setBriefTimedOutMessage] = useState(false);
  const [copyStatus, setCopyStatus] = useState<null | 'success' | 'error'>(
    null,
  );

  const { copy } = useCopyToClipboard({
    onSuccess: () => setCopyStatus('success'),
    onError: () => setCopyStatus('error'),
    onClear: () => setCopyStatus(null),
  });

  const navigationRefs = useRef<any>({});

  useEffect(() => {
    if (entryElement.intersectionRatio < 1) {
      setShowShadow(true);
    } else {
      setShowShadow(false);
    }
  }, [entryElement]);

  const handleHashChange = useCallback(() => {
    if (_.includes(window.location.hash, '#brief')) {
      setIsOpen(enabled);
    } else {
      setIsOpen(false);
    }
  }, [userDetails, enabled]);

  useEffect(() => {
    window.addEventListener('popstate', handleHashChange);
    if (_.includes(window.location.hash, '#brief') && enabled) {
      handleHashChange();
    }
    return () => {
      window.removeEventListener('popstate', handleHashChange);
    };
  }, [handleHashChange]);

  function closeModal(exitType?: string) {
    if (exitType !== 'submit') {
      logger.report(
        marketplaceBriefGeneralAction({
          briefType: 'compact',
          action_type: 'exit/cancel',
          sessionId,
          origin,
          entry,
        }),
      );
    }
    history.replaceState(history.state, '', '#');
    setIsOpen(false);
    setBriefTimedOutMessage(false);
  }

  useEffect(() => {
    try {
      if (location.hash) {
        const sectionId = location.hash.slice(1);
        if (document && document.getElementById(sectionId)) {
          const offset =
            document.getElementById(sectionId)!.getBoundingClientRect().top +
            105;
          animateScroll.scrollMore(offset, {
            duration: 1500,
            delay: 100,
            smooth: true,
          });
        }
      }
    } catch (err) {
      errorMonitor.captureException(err as Error);
    }
  }, [errorMonitor]);

  const [showBack, setShowBack] = useState(false);
  useEffect(() => {
    setShowBack(document.referrer.indexOf('hire') > -1);
  }, []);

  function onContactClicked() {
    logger.report(
      copyOfExploreResultsPageGenericClick({
        clickType: 'connect',
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
        data: '',
      }),
    );
    history.replaceState(history.state, '', '#brief');
    handleHashChange();
  }

  function headerOnClick() {
    logger.report(
      copyOfExploreResultsPageGenericClick({
        clickType: 'marketplace top button',
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
        data: '',
      }),
    );
  }

  function scrollToReviewsSection() {
    if (navigationRefs.current) {
      const offset = isMobileView ? 130 : 100;
      const targetPosition =
        navigationRefs.current[
          Sections.SERVICES_AND_REVIEWS
        ].getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: targetPosition - offset,
        behavior: 'smooth',
      });
    }
  }

  useEffect(() => {
    if (appStatus === 'initialized' && !isFirstLoad) {
      logger.report(
        partnersProfilePagePageLoaded({
          sessionId,
          origin: origin || document.referrer,
          entry,
          exploreVersion: 'V2',
          provider_id: partnerProfile.id,
        }),
      );
      setIsFirstLoad(true);
    }
  }, [appStatus, entry, isFirstLoad, origin, partnerProfile.id, sessionId]);

  const contactStatus = showSuccessNotification
    ? 'success'
    : showErrorNotification
    ? 'error'
    : null;

  const clearStatusMap: Record<'success' | 'error', () => void> = {
    success: () => setShowSuccessNotification(false),
    error: () => setShowErrorNotification(false),
  };

  if (isMobileView) {
    return (
      <Box
        display="block"
        dataHook="mobile-profile-page"
        className={s.mobileContainer}
      >
        {!isRenderedInModal ? (
          <Header
            onClick={headerOnClick}
            pageName="profile page"
            sessionId={sessionId}
          />
        ) : null}
        <ContactStatusToast
          status={contactStatus}
          clearStatus={contactStatus ? clearStatusMap[contactStatus] : () => {}}
          isMobileView={isMobileView}
        />
        <CopyNotification
          status={copyStatus}
          clearStatus={() => setCopyStatus(null)}
          isMobileView={true}
        />
        <BackToExplorer showBack={showBack} isMobileView={true} />
        <ContactPanel
          isMobileView={true}
          onContactClicked={onContactClicked}
          copy={copy}
        />
        <div
          ref={ref}
          className={
            showShadow ? `${s.stickyHeader} ${s.showShadow}` : s.stickyHeader
          }
        >
          <MiniContactPanel onContactClicked={onContactClicked} />
          <NavBar isMobileView={true} navigationRefs={navigationRefs} />
        </div>
        <Box marginLeft="24px" marginRight="24px" direction="vertical">
          <SectionWrapper sectionId={Sections.ABOUT} ref={navigationRefs}>
            <About isMobileView={true} />
            <Box paddingTop="24px" direction="vertical">
              <ProviderLocation showTitle={true} marginTop="18px" isMobile />
              <Box height="10px"></Box>
              <ProviderLanguages isMobile />
            </Box>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <Services isMobile={isMobileView} />
            </ErrorBoundary>
          </SectionWrapper>
          <SectionWrapper
            sectionId={Sections.CLIENT_PROJECTS}
            ref={navigationRefs}
          >
            <FeaturedProjects isMobileView={true} />
          </SectionWrapper>
          <SectionWrapper
            sectionId={Sections.SERVICES_AND_REVIEWS}
            ref={navigationRefs}
          >
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <StartingRates isMobileView={true} />
            </ErrorBoundary>
          </SectionWrapper>
          <SectionWrapper
            sectionId={Sections.SERVICES_AND_REVIEWS}
            ref={navigationRefs}
          >
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <ProjectsReviewsWithPaging
                isMobileView={isMobileView}
                onPageChange={scrollToReviewsSection}
              />
            </ErrorBoundary>
          </SectionWrapper>
        </Box>
        <CompactBrief
          isMobileView={true}
          closeModal={closeModal}
          isOpen={isOpen}
          setShowSuccessNotification={setShowSuccessNotification}
          setShowErrorNotification={setShowErrorNotification}
          setBriefTimedOutMessage={setBriefTimedOutMessage}
          briefTimedOutMessage={briefTimedOutMessage}
        />
      </Box>
    );
  }
  return (
    <Box display="block" dataHook="profile-page" backgroundColor="white">
      {!isRenderedInModal ? (
        <div className={s.stickyHeader}>
          <Header
            onClick={headerOnClick}
            pageName="profile page"
            sessionId={sessionId}
          />
        </div>
      ) : (
        <div className={s.stickyHeader}></div>
      )}
      <CopyNotification
        status={copyStatus}
        clearStatus={() => setCopyStatus(null)}
        isMobileView={false}
      />
      <ContactStatusToast
        status={contactStatus}
        clearStatus={contactStatus ? clearStatusMap[contactStatus] : () => {}}
        isMobileView={isMobileView}
      />
      <Box className={s.profilePageContent} direction="horizontal">
        <Box className={s.leftPanel} dataHook="left-panel" direction="vertical">
          <BackToExplorer showBack={showBack} isMobileView={false} />
          <ContactPanel
            isMobileView={false}
            onContactClicked={onContactClicked}
            copy={copy}
          />
        </Box>

        <Box direction="vertical" width="calc(100% - 302px)">
          <NavBar
            isMobileView={false}
            navigationRefs={navigationRefs}
            copy={copy}
            showBack={showBack}
          />
          <Box paddingLeft="96px" paddingRight="96px" maxWidth="1154px">
            <div style={{ width: '100%' }}>
              <SectionWrapper
                ref={navigationRefs}
                sectionId={Sections.ABOUT}
                max-width="770px"
              >
                <Box direction="horizontal">
                  <Box direction="vertical">
                    <About />
                    <ErrorBoundary FallbackComponent={ErrorFallback}>
                      <Services />
                    </ErrorBoundary>
                  </Box>
                  <Suspense fallback="">
                    <Certifications />
                  </Suspense>
                </Box>
              </SectionWrapper>

              <SectionWrapper
                sectionId={Sections.CLIENT_PROJECTS}
                ref={navigationRefs}
              >
                <FeaturedProjects isMobileView={false} />
              </SectionWrapper>
              <SectionWrapper
                sectionId={Sections.SERVICES_AND_REVIEWS}
                ref={navigationRefs}
              >
                <Box paddingBottom="35px">
                  <StartingRates />
                </Box>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <ProjectsReviewsWithPaging
                    isMobileView={isMobileView}
                    onPageChange={scrollToReviewsSection}
                  />
                </ErrorBoundary>
              </SectionWrapper>
              <TraceableTraderData />
            </div>
          </Box>
        </Box>
      </Box>
      <CompactBrief
        isMobileView={false}
        closeModal={closeModal}
        isOpen={isOpen}
        setShowSuccessNotification={setShowSuccessNotification}
        setShowErrorNotification={setShowErrorNotification}
        setBriefTimedOutMessage={setBriefTimedOutMessage}
        briefTimedOutMessage={briefTimedOutMessage}
      />
    </Box>
  );
};

export default Profile;
